import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "../components/Seo";
import useReveal from "../hooks/useReveal";
import { useEstimate } from "../components/EstimateContext";
import usePublicData from "../hooks/usePublicData";
import { TESTIMONIALS, COMPANY } from "../mock";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Honeypot } from "../components/Honeypot";
import {
  LayoutGrid, DoorOpen, PanelTop, ChefHat, Bath, BedDouble, Lightbulb, Wind, ShieldCheck, Layers,
  DollarSign, Users, Clock, MessageSquare, Search, FileText, FileCheck, Hammer, CheckCircle2,
  Plus, Minus, ArrowRight, Check, Star, Phone,
} from "lucide-react";
import {
  IMG, INCLUDED, USES, REQUIREMENTS, PROCESS, SUITE_SPACES, WHY, FAQ,
} from "../data/legalSuitesData";

const ICONS = { LayoutGrid, DoorOpen, PanelTop, ChefHat, Bath, BedDouble, Lightbulb, Wind, ShieldCheck, Layers, DollarSign, Users, Clock, MessageSquare, Search, FileText, FileCheck, Hammer, CheckCircle2 };

const Heading = ({ eyebrow, title, sub, center, light }) => (
  <div className={`${center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"} mb-12 reveal`}>
    <div className={`flex items-center gap-3 mb-4 ${center ? "justify-center" : ""}`}>
      <span className="h-px w-10 bg-[#2f9fe6]" />
      <span className={`tracking-[0.24em] text-xs font-semibold uppercase ${light ? "text-[#7fb8de]" : "text-[#2f9fe6]"}`}>{eyebrow}</span>
      {center && <span className="h-px w-10 bg-[#2f9fe6]" />}
    </div>
    <h2 className={`font-display text-3xl sm:text-4xl font-bold leading-tight ${light ? "text-white" : "text-[#0f2740]"}`}>{title}</h2>
    {sub && <p className={`text-lg mt-4 ${light ? "text-[#a9c6dd]" : "text-[#6b7683]"}`}>{sub}</p>}
  </div>
);

function SuiteForm() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [website, setWebsite] = useState("");
  const [f, setF] = useState({ name: "", phone: "", email: "", location: "", condition: "", size: "", use: "", rooms: "", timeline: "", budget: "", message: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const cls = "w-full px-4 py-3 rounded-xl bg-white border border-white/20 text-[#0f2740] outline-none focus:ring-2 focus:ring-[#2f9fe6]/50 placeholder:text-[#9aa4af]";

  const submit = async (e) => {
    e.preventDefault();
    const message = [
      `Property / neighbourhood: ${f.location || "—"}`,
      `Current basement condition: ${f.condition || "—"}`,
      `Approx size: ${f.size || "—"}`,
      `Intended use: ${f.use || "—"}`,
      `Desired rooms: ${f.rooms || "—"}`,
      `Target timeline: ${f.timeline || "—"}`,
      `Approx budget: ${f.budget || "—"}`,
      `Message: ${f.message || "—"}`,
    ].join("\n");
    try {
      await api.post("/leads", { name: f.name, email: f.email, phone: f.phone, service: "legal-suites", message, source: "legal-suites-page", website });
      setSubmitted(true);
      toast({ title: "Request received", description: "We'll reach out to discuss your suite options." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or call us.", variant: "destructive" });
    }
  };

  if (submitted) {
    return (
      <div className="bg-white/10 rounded-2xl p-10 text-center border border-white/15" data-testid="suite-form-success">
        <CheckCircle2 className="w-14 h-14 text-[#2f9fe6] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Thank you!</h3>
        <p className="text-[#a9c6dd]">Your request has been received. Our team will contact you soon to discuss your Edmonton basement suite.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white/5 rounded-2xl p-6 sm:p-8 border border-white/10 space-y-4" data-testid="suite-form">
      <Honeypot value={website} onChange={setWebsite} />
      <div className="grid sm:grid-cols-2 gap-4">
        <input required className={cls} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Name" data-testid="sf-name" />
        <input required className={cls} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" data-testid="sf-phone" />
        <input required type="email" className={cls} value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" data-testid="sf-email" />
        <input className={cls} value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="Property address or neighbourhood" data-testid="sf-location" />
        <select className={cls} value={f.condition} onChange={(e) => set("condition", e.target.value)} data-testid="sf-condition">
          <option value="">Current basement condition…</option>
          <option>Unfinished</option>
          <option>Partially finished</option>
          <option>Existing (unauthorized) suite</option>
          <option>Existing legal suite</option>
        </select>
        <input className={cls} value={f.size} onChange={(e) => set("size", e.target.value)} placeholder="Approximate basement size (sq ft)" data-testid="sf-size" />
        <input className={cls} value={f.use} onChange={(e) => set("use", e.target.value)} placeholder="Intended use (rental, family…)" data-testid="sf-use" />
        <input className={cls} value={f.rooms} onChange={(e) => set("rooms", e.target.value)} placeholder="Desired rooms" data-testid="sf-rooms" />
        <input className={cls} value={f.timeline} onChange={(e) => set("timeline", e.target.value)} placeholder="Target timeline" data-testid="sf-timeline" />
        <input className={cls} value={f.budget} onChange={(e) => set("budget", e.target.value)} placeholder="Approximate budget (optional)" data-testid="sf-budget" />
      </div>
      <textarea rows={3} className={cls} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="Anything else you'd like us to know?" data-testid="sf-message" />
      <button type="submit" className="w-full py-3.5 rounded-full bg-[#2f9fe6] text-white font-semibold hover:bg-[#1f82c7] transition-colors" data-testid="sf-submit">Request a Free Estimate</button>
      <a href={COMPANY.phoneHref} className="flex items-center justify-center gap-2 text-sm text-[#a9c6dd] hover:text-white transition-colors"><Phone className="w-4 h-4" /> Or call us: {COMPANY.phone}</a>
    </form>
  );
}

export default function LegalSuites() {
  useReveal();
  const { openEstimate } = useEstimate();
  const [openFaq, setOpenFaq] = useState(0);
  const { data: reviews } = usePublicData("/reviews", TESTIMONIALS);
  useReveal([reviews]);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Legal Basement Suite Development",
    provider: { "@type": "HomeAndConstructionBusiness", name: "Classic Interior Renovations LTD", areaServed: "Edmonton, Alberta" },
    areaServed: "Edmonton, Alberta",
    description: "Legal basement suite development in Edmonton — kitchens, bathrooms, bedrooms, entrances, fire separation and permit-ready planning.",
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://classic-interior-renovations.com/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://classic-interior-renovations.com/services" },
      { "@type": "ListItem", position: 3, name: "Legal Basement Suites", item: "https://classic-interior-renovations.com/services/legal-suites" },
    ],
  };

  return (
    <>
      <Seo title="Legal Basement Suites Edmonton" path="/services/legal-suites"
        description="Legal basement suite development in Edmonton, including kitchens, bathrooms, bedrooms, entrances, fire separation and permit-ready planning."
        image={IMG.hero} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Modern legal basement suite in Edmonton with kitchen and living area" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f2740]/95 via-[#0f2740]/82 to-[#0f2740]/45" />
        </div>
        <div className="container-x relative z-10 pt-28 pb-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#2f9fe6]" />
            <span className="text-[#bfe0f5] tracking-[0.26em] text-xs font-medium uppercase">Legal Basement Suites</span>
          </div>
          <h1 className="font-display text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6 max-w-3xl">
            Legal Basement Suites in <span className="font-bold text-[#2f9fe6]">Edmonton</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mb-7">
            Transform your basement into a comfortable, code-compliant secondary suite designed for rental income, multigenerational living and long-term property value.
          </p>
          <div className="flex flex-wrap gap-3 mb-9">
            {["Permit-ready planning", "Code-compliant construction", "Complete project management"].map((b) => (
              <span key={b} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15 text-white text-sm font-medium">
                <Check className="w-4 h-4 text-[#2f9fe6]" /> {b}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-4">
            <button data-testid="hero-estimate-btn" onClick={openEstimate} className="group px-8 py-4 rounded-full bg-[#2f9fe6] text-white font-semibold flex items-center gap-2 hover:bg-[#1f82c7] transition-all">
              Get a Free Estimate <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <Link to="/projects" className="px-8 py-4 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">View Our Projects</Link>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 bg-white">
        <div className="container-x max-w-3xl text-center reveal">
          <Heading center eyebrow="Overview" title="A complete basement suite, built with confidence" />
          <p className="text-[#4a5560] text-lg leading-relaxed mb-6">
            Classic Interior Renovations helps Edmonton homeowners turn unfinished and outdated basements into functional secondary suites. We coordinate the complete renovation — from layout planning and framing to kitchens, bathrooms, fire separation and final finishing.
          </p>
          <p className="text-[#6b7683] italic">
            Every property and project is different. Permit and construction requirements are confirmed after reviewing the home and proposed layout.
          </p>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x">
          <Heading center eyebrow="Scope" title="What's included" sub="Everything needed to create a complete, self-contained suite." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {INCLUDED.map((it, i) => {
              const Icon = ICONS[it.icon];
              return (
                <div key={it.title} className={`reveal delay-${(i % 5) + 1} bg-white rounded-2xl p-6 border border-[#eef1f4] hover:shadow-lg hover:-translate-y-1 transition-all`}>
                  <div className="w-12 h-12 rounded-xl bg-[#eaf5fd] flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-[#2f9fe6]" /></div>
                  <h3 className="font-display text-base font-semibold text-[#0f2740] mb-1.5">{it.title}</h3>
                  <p className="text-[#6b7683] text-sm leading-relaxed">{it.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* USES */}
      <section className="py-24 bg-[#0f2740] text-white">
        <div className="container-x">
          <Heading center light eyebrow="Your Goals" title="Designed around your goals" />
          <div className="grid md:grid-cols-3 gap-6">
            {USES.map((u, i) => {
              const Icon = ICONS[u.icon];
              return (
                <div key={u.title} className={`reveal delay-${(i % 3) + 1} bg-white/5 rounded-2xl p-8 border border-white/10`}>
                  <div className="w-14 h-14 rounded-xl bg-[#2f9fe6]/15 flex items-center justify-center mb-5"><Icon className="w-7 h-7 text-[#2f9fe6]" /></div>
                  <h3 className="font-display text-xl font-semibold mb-2">{u.title}</h3>
                  <p className="text-[#a9c6dd] leading-relaxed">{u.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* EDMONTON REQUIREMENTS */}
      <section className="py-24 bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-left">
            <Heading eyebrow="Local Requirements" title="Planning a legal suite in Edmonton" sub="Depending on your property and layout, a project may involve:" />
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {REQUIREMENTS.map((r) => (
                <div key={r} className="flex items-start gap-3 bg-[#f7f9fb] rounded-xl px-4 py-3 border border-[#eef1f4]">
                  <span className="w-6 h-6 rounded-full bg-[#2f9fe6] flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></span>
                  <span className="text-[#0f2740] text-sm font-medium">{r}</span>
                </div>
              ))}
            </div>
            <p className="text-[#6b7683] text-sm italic mb-4">
              Final requirements depend on the property, existing construction, proposed layout and current municipal and provincial regulations.
            </p>
            <a href="https://www.edmonton.ca/residential_neighbourhoods/secondary-suites" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#2f9fe6] font-semibold hover:gap-3 transition-all">
              City of Edmonton: Secondary Suites <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="reveal-right">
            <div className="shape-arch-top"><img src={IMG.entrance} alt="Separate exterior entrance to an Edmonton basement suite" className="w-full h-[520px] object-cover" /></div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 bg-[#f2ede4]">
        <div className="container-x">
          <Heading center eyebrow="Our Process" title="Our legal suite process" sub="A clear, coordinated path from first call to final walkthrough." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROCESS.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <div key={p.title} className={`reveal delay-${(i % 3) + 1} bg-white rounded-2xl p-7 border border-[#e5ddce]`}>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-12 h-12 rounded-full bg-[#eaf5fd] flex items-center justify-center"><Icon className="w-6 h-6 text-[#2f9fe6]" /></div>
                    <div className="text-[#2f9fe6] font-display font-bold text-sm">STEP {i + 1}</div>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[#0f2740] mb-2">{p.title}</h3>
                  <p className="text-[#6b7683] text-sm">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SUITE SPACES (illustrative) */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Inside a Suite" title="What a complete suite includes" sub="Illustrative examples of the spaces that make up a self-contained secondary suite." />
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {SUITE_SPACES.map((s, i) => (
              <div key={s.label} className={`reveal delay-${(i % 5) + 1} relative group shape-soft h-56`}>
                <img src={s.img} alt={s.label} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740]/85 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4"><span className="text-white font-medium text-sm">{s.label}</span></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x">
          <Heading center eyebrow="Why Us" title="Why Edmonton homeowners choose us" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {WHY.map((w, i) => (
              <div key={w} className={`reveal delay-${(i % 3) + 1} flex items-center gap-3 bg-white rounded-xl px-5 py-4 border border-[#eef1f4]`}>
                <span className="w-7 h-7 rounded-full bg-[#2f9fe6] flex items-center justify-center shrink-0"><Check className="w-4 h-4 text-white" /></span>
                <span className="font-medium text-[#0f2740]">{w}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="container-x max-w-3xl">
          <Heading center eyebrow="FAQ" title="Legal suite FAQ" sub="Short answers to common questions — not a substitute for official City of Edmonton guidance." />
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <div key={i} className="border border-[#e7e9ec] rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left" data-testid={`faq-${i}`}>
                  <span className={`font-display font-semibold ${openFaq === i ? "text-[#2f9fe6]" : "text-[#0f2740]"}`}>{f.q}</span>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${openFaq === i ? "bg-[#2f9fe6] text-white" : "bg-[#f2f5f8] text-[#0f2740]"}`}>{openFaq === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</span>
                </button>
                <div className={`overflow-hidden transition-all duration-400 ${openFaq === i ? "max-h-[560px]" : "max-h-0"}`}><p className="px-6 pb-5 text-[#6b7683] leading-relaxed">{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      {reviews && reviews.length > 0 && (
        <section className="py-24 bg-[#0f2740] text-white">
          <div className="container-x">
            <Heading center light eyebrow="Reviews" title="What our clients say" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((r, i) => (
                <div key={r.id || r.name} className={`reveal delay-${(i % 3) + 1} bg-white/5 rounded-2xl p-7 border border-white/10`}>
                  <div className="flex mb-3">{[...Array(r.rating || 5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-[#2f9fe6] text-[#2f9fe6]" />)}</div>
                  <p className="text-[#dbe7f1] text-sm leading-relaxed mb-4">“{r.text}”</p>
                  <div className="text-white font-semibold text-sm">{r.name}</div>
                  <div className="text-[#7fb8de] text-xs">{r.role}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED LINKS */}
      <section className="py-16 bg-white border-t border-[#eef1f4]">
        <div className="container-x">
          <div className="flex flex-wrap items-center justify-center gap-3 reveal">
            <span className="text-[#6b7683] mr-2">Explore more:</span>
            {[["Basement Development", "/services/basement"], ["Bathroom Renovations", "/services/bathroom"], ["Kitchen Renovations", "/services/kitchen"], ["Projects", "/projects"], ["Contact", "/contact"]].map(([label, to]) => (
              <Link key={to} to={to} className="px-5 py-2.5 rounded-full bg-[#f2f5f8] text-[#0f2740] font-medium text-sm hover:bg-[#2f9fe6] hover:text-white transition-colors">{label}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA + FORM */}
      <section className="py-24 bg-[#0f2740] text-white">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-start">
          <div className="reveal-left lg:sticky lg:top-28">
            <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#7fb8de] tracking-[0.24em] text-xs font-semibold uppercase">Get Started</span></div>
            <h2 className="font-display text-3xl sm:text-5xl font-bold mb-5">Ready to explore your basement suite options?</h2>
            <p className="text-[#a9c6dd] text-lg">Tell us about your Edmonton property and how you would like to use the basement. We'll discuss the space, your priorities and the next steps for creating a functional secondary suite.</p>
          </div>
          <div className="reveal-right"><SuiteForm /></div>
        </div>
      </section>
    </>
  );
}
